/**
 * Map Utilities
 * Centralized utilities for map-related operations
 */

/**
 * Validates if the given latitude and longitude coordinates are valid
 * @param {number|string} lat - Latitude value
 * @param {number|string} lng - Longitude value
 * @returns {boolean} - True if coordinates are valid, false otherwise
 */
export const isValidCoordinate = (lat, lng) => {
  // Check if values are null or undefined
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return false;
  }
  
  // Convert to numbers if they're strings
  const numLat = typeof lat === 'string' ? parseFloat(lat) : lat;
  const numLng = typeof lng === 'string' ? parseFloat(lng) : lng;
  
  // Check if values are numbers and not NaN
  if (typeof numLat !== 'number' || typeof numLng !== 'number' || 
      isNaN(numLat) || isNaN(numLng)) {
    return false;
  }
  
  // Check if values are within valid geographic ranges
  // Latitude: -90 to 90, Longitude: -180 to 180
  if (numLat < -90 || numLat > 90 || numLng < -180 || numLng > 180) {
    return false;
  }
  
  return true;
};

/**
 * Validates coordinates and logs warnings for invalid ones
 * @param {number|string} lat - Latitude value
 * @param {number|string} lng - Longitude value
 * @param {string} context - Context string for logging (e.g., component name)
 * @returns {boolean} - True if coordinates are valid, false otherwise
 */
export const isValidCoordinateWithLogging = (lat, lng, context = 'MapUtils') => {
  const isValid = isValidCoordinate(lat, lng);
  
  if (!isValid) {
    console.warn(`Invalid coordinates detected in ${context}:`, { lat, lng });
  }
  
  // Special case: log warning for (0,0) coordinates as they might be default/error values
  if (isValid && lat === 0 && lng === 0) {
    console.warn(`Coordinate at (0,0) detected in ${context} - might be default/error value`);
  }
  
  return isValid;
};

/**
 * Filters an array of objects to only include those with valid coordinates
 * @param {Array} items - Array of objects with lat/lng properties
 * @param {string} latKey - Property name for latitude (default: 'lat')
 * @param {string} lngKey - Property name for longitude (default: 'lng')
 * @param {string} context - Context string for logging
 * @returns {Array} - Filtered array with only valid coordinates
 */
export const filterValidCoordinates = (items = [], latKey = 'lat', lngKey = 'lng', context = 'MapUtils') => {
  return items.filter(item => {
    const lat = item[latKey];
    const lng = item[lngKey];
    const isValid = isValidCoordinate(lat, lng);
    
    if (!isValid) {
      console.warn(`Filtering out item with invalid coordinates in ${context}:`, {
        item,
        lat,
        lng
      });
    }
    
    return isValid;
  });
};

/**
 * Safely extends Google Maps bounds with validated coordinates
 * @param {google.maps.LatLngBounds} bounds - Google Maps bounds object
 * @param {number|string} lat - Latitude value
 * @param {number|string} lng - Longitude value
 * @param {string} context - Context string for error logging
 * @returns {boolean} - True if bounds was extended, false if coordinates were invalid
 */
export const safeExtendBounds = (bounds, lat, lng, context = 'MapUtils') => {
  if (!isValidCoordinate(lat, lng)) {
    console.warn(`Cannot extend bounds with invalid coordinates in ${context}:`, { lat, lng });
    return false;
  }
  
  try {
    bounds.extend(new window.google.maps.LatLng(lat, lng));
    return true;
  } catch (error) {
    console.error(`Error extending bounds in ${context}:`, {
      lat,
      lng,
      error: error.message
    });
    return false;
  }
};

/**
 * Creates a Google Maps LatLng object with validation
 * @param {number|string} lat - Latitude value
 * @param {number|string} lng - Longitude value
 * @param {string} context - Context string for error logging
 * @returns {google.maps.LatLng|null} - LatLng object or null if invalid
 */
export const createSafeLatLng = (lat, lng, context = 'MapUtils') => {
  if (!isValidCoordinate(lat, lng)) {
    console.warn(`Cannot create LatLng with invalid coordinates in ${context}:`, { lat, lng });
    return null;
  }
  
  try {
    return new window.google.maps.LatLng(lat, lng);
  } catch (error) {
    console.error(`Error creating LatLng in ${context}:`, {
      lat,
      lng,
      error: error.message
    });
    return null;
  }
}; 


export const findEnvBasedMapKey = () => {
  return process.env.UI_GOOGLE_API_KEY;
};
